/**
 * Treatment plan settings, MCOS wide (SUPERADMIN only).
 *
 * The OPR markup per drug category, the margin floor and the rounding are
 * placeholders in the Treatment Plan spec v1.4 until MCOS decides them, so they
 * live here rather than in a file a restart would reapply. Saving rewrites
 * every clinic's starting prices at once.
 *
 * The two Clinical Twin minimums are TL-003: the patient's "What to expect"
 * estimate shows only above them.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Button, Card, ErrorState, Field, LoadingRows, PageHeader, inputClass } from '../components/ui';
import { IconCheck } from '../components/icons';

interface PlanSettings {
  pricing: {
    marginFloor: number;
    roundTo: number;
    markups: Array<{ category: string; markup: number; updatedAt: string; updatedBy: string | null }>;
  };
  roundToChoices: number[];
  timeline: { minMatched: number; minReached: number };
}

const KEY = ['platform', 'plan-settings'];

/** 1.5 is shown as 150, because a markup is read as a percentage of cost. */
const toPercent = (fraction: number) => String(Math.round(fraction * 1000) / 10);
const fromPercent = (text: string) => Number(text) / 100;
const isWhole = (text: string) => /^\d+$/.test(text.trim());
const isPercent = (text: string, max: number) => text.trim() !== '' && Number.isFinite(Number(text)) && Number(text) >= 0 && Number(text) <= max;

export function PlatformPlanSettingsPage() {
  const settings = useQuery({ queryKey: KEY, queryFn: () => api.get<PlanSettings>('/platform/plan-settings') });

  return (
    <div className="mx-auto max-w-[920px]">
      <PageHeader title="Treatment plans" subtitle="Settings that apply to every clinic." />
      {settings.isLoading && <LoadingRows rows={4} />}
      {settings.error && <ErrorState error={settings.error} retry={() => void settings.refetch()} />}
      {settings.data && (
        <div className="space-y-5">
          <PricingCard data={settings.data} />
          <TimelineCard data={settings.data} />
        </div>
      )}
    </div>
  );
}

function SavedNote({ show }: { show: boolean }) {
  if (!show) return null;
  return (
    <span className="flex items-center gap-1 text-sm text-emerald-700" role="status">
      <IconCheck size={14} /> Saved
    </span>
  );
}

function useSave(onDone: () => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => api.put<PlanSettings>('/platform/plan-settings', body),
    onSuccess: (data) => {
      qc.setQueryData(KEY, data);
      void qc.invalidateQueries({ queryKey: ['opr-items'] });
      onDone();
    },
  });
}

function PricingCard({ data }: { data: PlanSettings }) {
  const [markups, setMarkups] = useState<Record<string, string>>({});
  const [floor, setFloor] = useState('');
  const [roundTo, setRoundTo] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setMarkups(Object.fromEntries(data.pricing.markups.map((m) => [m.category, toPercent(m.markup)])));
    setFloor(toPercent(data.pricing.marginFloor));
    setRoundTo(String(data.pricing.roundTo));
  }, [data]);

  const save = useSave(() => {
    setSaved(true);
    window.setTimeout(() => setSaved(false), 2500);
  });

  const markupsValid = Object.values(markups).every((value) => isPercent(value, 1000));
  const floorValid = isPercent(floor, 90);

  return (
    <Card title="OPR starting prices" subtitle="Used for every drug a clinic has not yet priced from its own plans.">
      <div className="grid gap-4 sm:grid-cols-2">
        {data.pricing.markups.map((row) => (
          <Field
            key={row.category}
            label={`${row.category} markup`}
            hint="Percent added to the cost."
            error={markups[row.category] !== undefined && !isPercent(markups[row.category]!, 1000) ? 'Enter 0 to 1000' : undefined}
          >
            <div className="flex items-center gap-1.5">
              <input
                id={`markup-${row.category}`}
                value={markups[row.category] ?? ''}
                onChange={(e) => setMarkups({ ...markups, [row.category]: e.target.value })}
                className={inputClass}
                inputMode="decimal"
              />
              <span className="text-sm text-ink-muted">%</span>
            </div>
          </Field>
        ))}
        <Field label="Margin floor" hint="No starting price falls below cost plus this margin." error={!floorValid ? 'Enter 0 to 90' : undefined}>
          <div className="flex items-center gap-1.5">
            <input id="margin-floor" value={floor} onChange={(e) => setFloor(e.target.value)} className={inputClass} inputMode="decimal" />
            <span className="text-sm text-ink-muted">%</span>
          </div>
        </Field>
        <Field label="Round prices to">
          <select id="round-to" value={roundTo} onChange={(e) => setRoundTo(e.target.value)} className={inputClass}>
            {data.roundToChoices.map((choice) => (
              <option key={choice} value={choice}>
                ${choice}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button
          loading={save.isPending}
          disabled={!markupsValid || !floorValid}
          onClick={() =>
            save.mutate({
              marginFloor: fromPercent(floor),
              roundTo: Number(roundTo),
              markups: Object.fromEntries(Object.entries(markups).map(([category, value]) => [category, fromPercent(value)])),
            })
          }
        >
          Save prices
        </Button>
        <SavedNote show={saved} />
        {save.error && <span className="text-sm text-red-600">{save.error instanceof Error ? save.error.message : 'Could not save.'}</span>}
      </div>
    </Card>
  );
}

function TimelineCard({ data }: { data: PlanSettings }) {
  const [matched, setMatched] = useState('');
  const [reached, setReached] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setMatched(String(data.timeline.minMatched));
    setReached(String(data.timeline.minReached));
  }, [data]);

  const save = useSave(() => {
    setSaved(true);
    window.setTimeout(() => setSaved(false), 2500);
  });

  const matchedValid = isWhole(matched) && Number(matched) >= 1;
  const reachedValid = isWhole(reached) && Number(reached) >= 1 && (!matchedValid || Number(reached) <= Number(matched));

  return (
    <Card title="What to expect estimate" subtitle="The patient sees a time estimate only when there is enough Clinical Twin data.">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Matched patients, at least" error={!matchedValid ? 'Enter a whole number' : undefined}>
          <input id="timeline-matched" value={matched} onChange={(e) => setMatched(e.target.value)} className={inputClass} inputMode="numeric" />
        </Field>
        <Field label="Of them reached the goal, at least" error={!reachedValid ? 'A whole number, no more than matched patients' : undefined}>
          <input id="timeline-reached" value={reached} onChange={(e) => setReached(e.target.value)} className={inputClass} inputMode="numeric" />
        </Field>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button
          loading={save.isPending}
          disabled={!matchedValid || !reachedValid}
          onClick={() => save.mutate({ timeline: { minMatched: Number(matched), minReached: Number(reached) } })}
        >
          Save minimums
        </Button>
        <SavedNote show={saved} />
        {save.error && <span className="text-sm text-red-600">{save.error instanceof Error ? save.error.message : 'Could not save.'}</span>}
      </div>
    </Card>
  );
}
